import React, { useRef, useEffect } from 'react';
import { ChatHeading, ChatModalContainer, ResponseArea } from './chat.styled';
import { Composer } from './composer';
import useAIChat from './hooks/useAIChat';
import ChatMessage from './chatMessage';
import { AILoadingIndicator } from '../../loading-indicators/indicators.styled';
import { motion } from 'framer-motion';
import { rem } from '../../../utils/style';
import Heading from '../../typography';
import VoiceRecorder from '../../audio-recorder';
import 'mutationobserver-shim';
import styled from 'styled-components';

const InputAreaWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const ChatModal: React.FC<{ closeModal: () => void; isChatModalOpen: boolean }> = ({
  closeModal,
  isChatModalOpen,
}) => {
  const {
    inputText,
    chatHistory,
    setChatHistory,
    loading,
    initialSetupLoading,
    responseContainerRef,
    handleInputChange,
    handleApiCall,
    handleKeyPress,
    handleAudioApiCall, // Function for processing audio input
    scrollToBottom,
  } = useAIChat();

  const wrapperRef = useRef<any>(null);

  const handleMinimizeChat = () => {
    closeModal();
  };

  const handleCloseChat = () => {
    sessionStorage.removeItem('chat-session-id');
    setChatHistory([]);
    closeModal();
  };

  useEffect(() => {
    const observer = new MutationObserver(() => {
      scrollToBottom();
    });

    if (responseContainerRef.current) {
      observer.observe(responseContainerRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (isChatModalOpen) {
      scrollToBottom();
    }
  }, [isChatModalOpen]);

  return (
    <motion.div
      initial={false}
      animate={isChatModalOpen ? 'open' : 'closed'}
      style={{
        boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
        border: '1px solid rgb(220, 220, 220)',
        borderRadius: '10px',
      }}
      variants={{
        open: {
          clipPath: 'inset(0% 0% 0% 0% round 10px)',
          transition: {
            type: 'spring',
            bounce: 0,
            duration: 0.7,
          },
          position: 'fixed',
          width: `${rem(700)}`,
          height: `${rem(800)}`,
          bottom: `calc(${rem(20)})`,
          right: `${rem(60)}`,
          zIndex: 1000,
          opacity: 1,
          border: '1px solid rgb(220, 220, 220)',
        },
        closed: {
          clipPath: 'inset(10% 50% 90% 50% round 10px)',
          transition: {
            type: 'spring',
            bounce: 0,
            duration: 0.7,
          },
          position: 'fixed',
          bottom: `calc(${rem(20)})`,
          right: `${rem(20)}`,
          zIndex: 1000,
          opacity: 0,
          border: '1px solid rgb(220, 220, 220)',
        },
      }}>
      <ChatModalContainer ref={wrapperRef}>
        <ChatHeading className='modal-heading'>
          <Heading fontWeight={500} variant='h5' title='Chat with Wealthlane' />
          <div className='flex space-x-3'>
            <span className='flex modal-close' onClick={handleMinimizeChat}>
              <span className='material-symbols-outlined minimize-icon-color'>
                do_not_disturb_on
              </span>
            </span>
            <span className='flex modal-close' onClick={handleCloseChat}>
              <span className='material-symbols-outlined icon-color'>cancel</span>
            </span>
          </div>
        </ChatHeading>
        <ResponseArea ref={responseContainerRef}>
          {chatHistory.map((chat, index) => (
            <ChatMessage
              key={`chat-message-${index}`}
              message={{ id: index, text: chat.message, user: { name: chat.sender } }}
              right={chat.sender !== 'Wealthlane'}
              index={index}
              lastChangedIndex={null}
            />
          ))}
          {loading && (
            <ChatMessage
              message={{
                id: Math.random(),
                text: (
                  <AILoadingIndicator>
                    <div className='loader'></div>
                  </AILoadingIndicator>
                ),
                user: { name: '' },
              }}
              right={false}
              index={Math.random()}
              lastChangedIndex={null}
            />
          )}
        </ResponseArea>
        <InputAreaWrapper>
          <VoiceRecorder handleAudioApiCall={handleAudioApiCall} />
          <Composer
            inputText={inputText}
            handleInputChange={handleInputChange}
            handleKeyPress={handleKeyPress}
            handleApiCall={handleApiCall}
            loading={loading || initialSetupLoading}
            onSend={handleApiCall}
          />
        </InputAreaWrapper>
      </ChatModalContainer>
    </motion.div>
  );
};

export default ChatModal;
